import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

/**
 * SearchableSelect — combobox leve com busca/autocomplete por nome.
 * Estilo alinhado ao tema escuro dos filtros do Financeiro.
 *
 * Props:
 *  - value: string (id selecionado, '' = nenhum)
 *  - onChange: (value: string) => void
 *  - options: Array<{ value: string, label: string, group?: string }>
 *  - placeholder: string (rótulo quando nada selecionado)
 *  - testId: string (prefixo dos data-testid)
 */
export function SearchableSelect({ value, onChange, options = [], placeholder = 'Selecione...', testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-toggle`}
        className={`flex items-center justify-between gap-2 min-w-[170px] max-w-[230px] bg-[#1A1A1A] border rounded-lg px-3 py-2 text-sm outline-none transition-colors ${
          value ? 'border-yellow-500/60 text-white' : 'border-gray-700 text-gray-400'
        } hover:border-gray-600`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        {value ? (
          <X size={14} className="shrink-0 text-gray-400 hover:text-red-400" onClick={handleClear} data-testid={`${testId}-clear`} />
        ) : (
          <ChevronDown size={14} className="shrink-0 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-[260px] bg-[#111] border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-800">
            <Search size={14} className="text-gray-500 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome..."
              data-testid={`${testId}-search`}
              className="w-full bg-transparent text-sm text-white placeholder-gray-600 outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-gray-600">Nenhum resultado</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`${testId}-option-${o.value}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm truncate transition-colors ${
                    o.value === value ? 'bg-yellow-500/10 text-yellow-400' : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
